import { jsxs, jsx } from "react/jsx-runtime";
import { Star, Navigation, Phone, Globe } from "lucide-react";
import { useIsCompactVoiceLayout } from "../../hooks/useIsCompactVoiceLayout.js";
const WIDGET_KEYFRAMES = `
@keyframes voice-store-list-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
const CTA_ICON_PROPS = { size: 18, strokeWidth: 2, "aria-hidden": true };
function buildDirectionsUrl(s) {
  if (s.googleMapsUri) return s.googleMapsUri;
  const dest = encodeURIComponent(`${s.address}, ${s.city}, ${s.state} ${s.zipcode}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}
function normalizePhoneHref(phone) {
  const digits = phone.replace(/[^\d+]/g, "");
  return `tel:${digits}`;
}
function formatRating(rating, reviewCount) {
  if (rating == null) return "";
  const r = rating.toFixed(1);
  if (!reviewCount) return r;
  if (reviewCount >= 1e3) return `${r} · ${(reviewCount / 1e3).toFixed(reviewCount >= 1e4 ? 0 : 1)}k`;
  return `${r} · ${reviewCount}`;
}
const StoreCard = ({ store, isCompact }) => {
  const showRating = typeof store.rating === "number";
  const showOpenPill = typeof store.openNow === "boolean";
  const distLabel = `${store.distanceMiles.toFixed(1)} mi`;
  const openLabel = store.openNow ? store.openUntil ? `Open until ${store.openUntil}` : "Open now" : "Closed";
  const openColor = store.openNow ? "#059669" : "#9CA3AF";
  const openBg = store.openNow ? "#ECFDF5" : "#F3F4F6";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        gap: 14,
        padding: "14px 16px",
        borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)",
        background: "var(--voice-widget-card-bg, #FFFFFF)"
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: 60,
              height: 60,
              borderRadius: 10,
              flexShrink: 0,
              overflow: "hidden",
              background: "var(--voice-widget-chip-bg, #F3F4F6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative"
            },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                src: store.retailerLogoUrl,
                alt: store.retailerName,
                style: { width: 42, height: 42, objectFit: "contain" },
                onError: (e) => {
                  e.currentTarget.style.display = "none";
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }, children: [
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 8, justifyContent: "space-between" }, children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--voice-widget-fg, #111827)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0
                },
                title: store.storeName,
                children: store.storeName
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--voice-widget-muted, #6B7280)",
                  flexShrink: 0
                },
                children: distLabel
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                fontSize: 12,
                color: "var(--voice-widget-muted, #6B7280)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              },
              title: `${store.address}, ${store.city}, ${store.state} ${store.zipcode}`,
              children: [
                store.address,
                ", ",
                store.city,
                ", ",
                store.state
              ]
            }
          ),
          (showRating || store.priceLevel || showOpenPill) && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                flexWrap: "wrap"
              },
              children: [
                showRating && /* @__PURE__ */ jsxs(
                  "span",
                  {
                    style: {
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--voice-widget-fg, #111827)"
                    },
                    children: [
                      /* @__PURE__ */ jsx(Star, { size: 12, fill: "#F59E0B", stroke: "#F59E0B", "aria-hidden": true }),
                      formatRating(store.rating, store.userRatingCount)
                    ]
                  }
                ),
                store.priceLevel && /* @__PURE__ */ jsx(
                  "span",
                  {
                    style: {
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--voice-widget-muted, #6B7280)"
                    },
                    children: store.priceLevel
                  }
                ),
                showOpenPill && /* @__PURE__ */ jsx(
                  "span",
                  {
                    style: {
                      fontSize: 10,
                      fontWeight: 700,
                      color: openColor,
                      background: openBg,
                      padding: "2px 8px",
                      borderRadius: 999
                    },
                    children: openLabel
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 8, marginTop: "auto", paddingTop: 10 }, children: [
            /* @__PURE__ */ jsxs(
              "a",
              {
                href: buildDirectionsUrl(store),
                target: "_blank",
                rel: "noopener noreferrer",
                style: ctaButtonStyle("primary", isCompact),
                "aria-label": "Directions",
                title: isCompact ? "Directions" : void 0,
                children: [
                  /* @__PURE__ */ jsx(Navigation, { ...CTA_ICON_PROPS }),
                  !isCompact && /* @__PURE__ */ jsx("span", { children: "Directions" })
                ]
              }
            ),
            store.phone && /* @__PURE__ */ jsxs(
              "a",
              {
                href: normalizePhoneHref(store.phone),
                style: ctaButtonStyle("secondary", isCompact),
                title: store.phone,
                "aria-label": `Call ${store.phone}`,
                children: [
                  /* @__PURE__ */ jsx(Phone, { ...CTA_ICON_PROPS }),
                  !isCompact && /* @__PURE__ */ jsx("span", { children: "Call" })
                ]
              }
            ),
            store.website && /* @__PURE__ */ jsxs(
              "a",
              {
                href: store.website,
                target: "_blank",
                rel: "noopener noreferrer",
                style: ctaButtonStyle("secondary", isCompact),
                "aria-label": "Website",
                title: isCompact ? "Website" : void 0,
                children: [
                  /* @__PURE__ */ jsx(Globe, { ...CTA_ICON_PROPS }),
                  !isCompact && /* @__PURE__ */ jsx("span", { children: "Website" })
                ]
              }
            )
          ] })
        ] })
      ]
    }
  );
};
function ctaButtonStyle(variant, iconOnly) {
  const isPrimary = variant === "primary";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 36,
    width: iconOnly ? 36 : void 0,
    padding: iconOnly ? 0 : "0 12px",
    fontSize: 12,
    fontWeight: 600,
    textDecoration: "none",
    lineHeight: 1.2,
    borderRadius: 8,
    color: isPrimary ? "var(--voice-widget-button-fg, #FFFFFF)" : "var(--voice-widget-fg, #111827)",
    background: isPrimary ? "var(--voice-widget-button-bg, #111827)" : "var(--voice-widget-chip-bg, #F3F4F6)",
    border: isPrimary ? "none" : "1px solid var(--voice-widget-border, #E5E7EB)",
    flex: iconOnly ? "0 0 auto" : 1,
    minWidth: 0,
    boxSizing: "border-box"
  };
}
const StoreListWidget = ({ data }) => {
  const total = data.totalLocalStores;
  const isCompact = useIsCompactVoiceLayout();
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: 520,
        margin: "0 auto",
        background: "var(--voice-widget-card-bg, #FFFFFF)",
        border: "1px solid var(--voice-widget-border, #E5E7EB)",
        borderRadius: 14,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        overflow: "hidden",
        animation: "voice-store-list-fade-in 0.28s ease-out both",
        display: "flex",
        flexDirection: "column"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              padding: "14px 16px 10px",
              borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)"
            },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: "var(--voice-widget-muted, #6B7280)"
                  },
                  children: "Nearby stores"
                }
              ),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--voice-widget-fg, #111827)",
                    marginTop: 2
                  },
                  children: [
                    "Showing ",
                    data.stores.length,
                    " closest of ",
                    total,
                    " near ",
                    data.resolvedLocation.label
                  ]
                }
              ),
              data.resolvedLocation.source === "ip" && /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: 10,
                    color: "var(--voice-widget-muted, #6B7280)",
                    marginTop: 2
                  },
                  children: "Detected from your IP — say a different city or zip if this isn't right."
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { children: data.stores.map((store) => /* @__PURE__ */ jsx(StoreCard, { store, isCompact }, store.storeId)) })
      ]
    }
  );
};
export {
  StoreListWidget
};
//# sourceMappingURL=StoreListWidget.js.map
